import React, { useMemo } from 'react';
import { AvailabilityReportDetail } from '@/types';
import { useAvailabilityReportStore } from '@/stores';
import { useToastStore } from '@/stores';
import {
  useUpdateAvailabilityItems,
  useResetAvailabilityReport,
  useSendAvailabilityReport,
  useDownloadAvailabilityReport,
} from '@/hooks/availability-report';
import { AvailabilityReportTable } from './AvailabilityReportTable';
import { ReportView, VELOCITY_COUNTERPARTY } from './ReportView';
import type { ReportStoreAdapter, ReportMutations } from './ReportView';
import { getErrorMessage } from '@/utils/get-error-message';

interface AvailabilityReportViewProps {
  report: AvailabilityReportDetail | undefined;
  isLoading: boolean;
  error: unknown;
  searchQuery?: string;
}

export const AvailabilityReportView: React.FC<AvailabilityReportViewProps> = ({
  report,
  isLoading,
  error,
  searchQuery,
}) => {
  const addToast = useToastStore((s) => s.addToast);

  // Build store adapter — memoized to preserve referential identity
  const isEditing = useAvailabilityReportStore((s) => s.isEditing);
  const localEdits = useAvailabilityReportStore((s) => s.localEdits);
  const newItems = useAvailabilityReportStore((s) => s.newItems);
  const selectedReportId = useAvailabilityReportStore((s) => s.selectedReportId);
  const enterEditMode = useAvailabilityReportStore((s) => s.enterEditMode);
  const cancelEdit = useAvailabilityReportStore((s) => s.cancelEdit);
  const updateItemQuantity = useAvailabilityReportStore((s) => s.updateItemQuantity);
  const updateItemCounterpartyProductId = useAvailabilityReportStore((s) => s.updateItemCounterpartyProductId);
  const toggleItemDeleted = useAvailabilityReportStore((s) => s.toggleItemDeleted);
  const markItemDeleted = useAvailabilityReportStore((s) => s.markItemDeleted);
  const hasChanges = useAvailabilityReportStore((s) => s.hasChanges);
  const getChangedItems = useAvailabilityReportStore((s) => s.getChangedItems);
  const addNewItem = useAvailabilityReportStore((s) => s.addNewItem);
  const removeNewItem = useAvailabilityReportStore((s) => s.removeNewItem);
  const updateNewItemQuantity = useAvailabilityReportStore((s) => s.updateNewItemQuantity);
  const updateNewItemCounterpartyProductId = useAvailabilityReportStore((s) => s.updateNewItemCounterpartyProductId);
  const getNewItemsPayload = useAvailabilityReportStore((s) => s.getNewItemsPayload);

  const store: ReportStoreAdapter = useMemo(() => ({
    isEditing, localEdits, newItems, selectedReportId,
    enterEditMode, cancelEdit, updateItemQuantity, updateItemCounterpartyProductId, toggleItemDeleted, markItemDeleted,
    hasChanges, getChangedItems, addNewItem, removeNewItem, updateNewItemQuantity, updateNewItemCounterpartyProductId, getNewItemsPayload,
  }), [
    isEditing, localEdits, newItems, selectedReportId,
    enterEditMode, cancelEdit, updateItemQuantity, updateItemCounterpartyProductId, toggleItemDeleted, markItemDeleted,
    hasChanges, getChangedItems, addNewItem, removeNewItem, updateNewItemQuantity, updateNewItemCounterpartyProductId, getNewItemsPayload,
  ]);

  // Mutation hooks
  const updateItemsMutation = useUpdateAvailabilityItems({
    onSuccess: () => {
      store.cancelEdit();
      addToast('success', 'Report saved');
    },
    onError: (error) => {
      addToast('error', getErrorMessage(error, 'Failed to save report'));
    },
  });

  const resetMutation = useResetAvailabilityReport({
    onSuccess: () => {
      store.cancelEdit();
      addToast('success', 'Report reset to original');
    },
    onError: () => {
      addToast('error', 'Failed to reset report');
    },
  });

  const sendMutation = useSendAvailabilityReport({
    onSuccess: () => {
      addToast('success', 'Report sent');
    },
    onError: () => {
      addToast('error', 'Failed to send report');
    },
  });

  const { download: downloadCsv } = useDownloadAvailabilityReport();

  const mutations: ReportMutations = {
    updateItems: updateItemsMutation,
    reset: resetMutation,
    send: sendMutation,
    download: downloadCsv,
  };

  return (
    <ReportView
      report={report}
      isLoading={isLoading}
      error={error}
      store={store}
      mutations={mutations}
      TableComponent={AvailabilityReportTable}
      searchQuery={searchQuery}
      resetDialogMessage="Reset all changes to original values? Manually added items will be removed."
      sendDialogMessage="Send this availability report to the counterparty now? This action cannot be undone."
      filenamePrefix="VCEX-EXA-568545-AVAILABLE"
      inventoryCounterparty={VELOCITY_COUNTERPARTY}
      counterparty="velocity"
    />
  );
};
